import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { GuideType } from "@mapmycustomers/shared/enum/guides";
import { Guide, GuideUserStatus } from "@mapmycustomers/shared/types/entity";
import User from "@mapmycustomers/shared/types/User";

import { getGuides, getGuidesStatus, isLoading } from "@app/scene/onboarding/store";
import getGuidesStatusMap from "@app/scene/onboarding/utils/getGuidesStatusMap";
import { RootState } from "@app/store/rootReducer";
import useInterval from "@app/util/hook/useInterval";

import BulkActions from "./components/OnboardingGuides/BulkActions";
import Guides from "./components/OnboardingGuides/Guides";
import GuidesFilterBar from "./components/OnboardingGuides/GuidesFilterBar";
import GuidesStats from "./components/OnboardingGuides/GuidesStats";
import Subheader from "./components/Subheader";
import ViewMode from "./enums/ViewMode";
import styles from "./OnboardingPageCommon.module.scss";
import { initialize, refreshGuidesStatus } from "./store/actions";

interface Props {
  guides: Guide[];
  guidesStatus: GuideUserStatus[];
  loading?: boolean;
  onInitialize: () => void;
  onRefreshGuideStatus: () => void;
}

export const OnboardingGuides: React.FC<Props> = ({
  guides,
  guidesStatus,
  loading,
  onInitialize,
  onRefreshGuideStatus,
}) => {
  useEffect(() => {
    onInitialize();
  }, [onInitialize]);

  useInterval(onRefreshGuideStatus, true, 15_000);

  const guidesStatusMap = useMemo(() => getGuidesStatusMap(guidesStatus), [guidesStatus]);

  const [selectedGuideIds, setSelectedGuideIds] = useState<Guide["id"][]>([]);
  const handleResetSelection = useCallback(() => setSelectedGuideIds([]), []);

  const [assigneeFilter, setAssigneeFilter] = useState<null | User["id"][]>([]);
  const [typeFilter, setTypeFilter] = useState<GuideType | undefined>();
  const [quickFilter, setQuickFilter] = useState<string>("");

  const handleResetFilters = useCallback(() => {
    setAssigneeFilter([]);
    setTypeFilter(undefined);
    setQuickFilter("");
  }, []);

  const filteredGuides = useMemo(() => {
    return guides.filter((guide) => {
      if (
        (typeFilter && guide.type !== typeFilter) ||
        (quickFilter && !guide.displayName.toLowerCase().includes(quickFilter.trim().toLowerCase()))
      ) {
        return false;
      }

      if (assigneeFilter === null) {
        return !(guide.id in guidesStatusMap);
      }

      return assigneeFilter.length
        ? (guidesStatusMap[guide.id] ?? []).some((guideStatus) =>
            assigneeFilter.includes(guideStatus.user.id)
          )
        : true;
    });
  }, [guides, assigneeFilter, typeFilter, quickFilter, guidesStatusMap]);

  return (
    <div className={styles.container}>
      <Subheader
        totalFilteredRecords={filteredGuides.length}
        totalRecords={guides.length}
        viewMode={ViewMode.GUIDES}
      />
      <div className={styles.content}>
        <GuidesFilterBar
          assigneeFilter={assigneeFilter}
          onAssigneeFilterChange={setAssigneeFilter}
          onQuickFilterChange={setQuickFilter}
          onTypeFilterChange={setTypeFilter}
          quickFilter={quickFilter}
          typeFilter={typeFilter}
        />
        <BulkActions guideIds={selectedGuideIds} onResetSelection={handleResetSelection} />
        <div className={styles.delimiter} />
        {guides.length > 0 && (
          <GuidesStats guides={filteredGuides} guidesStatusMap={guidesStatusMap} />
        )}
        <Guides
          guides={filteredGuides}
          guidesStatusMap={guidesStatusMap}
          loading={loading}
          onResetFilters={handleResetFilters}
          onSelectionChange={setSelectedGuideIds}
          selectedGuideIds={selectedGuideIds}
        />
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  guides: getGuides(state),
  guidesStatus: getGuidesStatus(state),
  loading: isLoading(state),
});

const mapDispatchToProps = {
  onInitialize: initialize.request,
  onRefreshGuideStatus: refreshGuidesStatus.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(OnboardingGuides);
